import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Modal, Button } from "react-bootstrap";
import { FormHandles } from "@unform/core";
import { format, parseISO } from 'date-fns';
import pt from 'date-fns/locale/pt';

import { ToastContext } from "../../contexts/toast/toastContext";
import DataTable from "../../components/DataTable";
import * as faturamentoService from "../../services/faturamento";
import { IFinanceiroTituloReceber } from "../../common/interfaces/faturamento.interfaces";
import DataNotFound from "../../components/DataNotFound";
import { getSingleErrorMessage } from "../../helpers/getSingleErrorMessage";

const Fatura: React.FC = () => {
  // Refs
  const formRef = useRef<FormHandles>(null);

  //Fields Typed
  const DataTableTS: any = DataTable;

  //Context
  const toastContext = useContext(ToastContext);

  //States
  const [tableData, setTableData] = useState<IFinanceiroTituloReceber[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [novo, setNovo] = useState(false);
  const [reloadGrid, setReloadGrid] = useState(false);

  //Effects
  useEffect(() => {
    (async () => {
      try {
        setTableLoading(true);

        const response = await faturamentoService.obterTitulos();
        setTableData(response);
      } catch (error) {
        toastContext.notification(getSingleErrorMessage(error), "error");
      } finally {
        setTableLoading(false);
      }
    })();
  }, [reloadGrid]);

  //Handles
  const handleClose = () => setShow(false);

  const iniciarDownload = useCallback((arquivo: Blob, nomeArquivo: string) => {
    const downloadUrl = window.URL.createObjectURL(new Blob([arquivo]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleObterBoleto = useCallback(async (titulo: IFinanceiroTituloReceber) => {
    try {
      setTableLoading(true);

      const gerarBoletoData = {
        id: titulo.idTitulo,
        ehTituloReceber: titulo.ehTituloReceber
      };

      const response = await faturamentoService.gerarBoleto(gerarBoletoData);
      let nomeArquivo = `${format(parseISO(titulo.vencimento.toString()), 'MMMM', { locale: pt }).toUpperCase()}-${format(parseISO(titulo.vencimento.toString()), 'yyyy', { locale: pt }).toUpperCase()}`;
      iniciarDownload(new Blob([response]), `${nomeArquivo}.pdf`);
    } catch (error) {
      toastContext.notification(getSingleErrorMessage(error), "error");
    } finally {
      setTableLoading(false);
    }
  }, []);

  const handleObterXMLNofaFiscal = async ({ idNotaFiscal }: IFinanceiroTituloReceber) => {
    try {
      setTableLoading(true);
      const response = await faturamentoService.xmlNotaFiscal(idNotaFiscal);
      iniciarDownload(new Blob([response]), `${idNotaFiscal}.xml`);
    } catch (error) {
      toastContext.notification(getSingleErrorMessage(error), "error");
    } finally {
      setTableLoading(false);
    }
  };

  const handleObterNofaFiscal = async ({ idNotaFiscal }: IFinanceiroTituloReceber) => {
    try {
      setTableLoading(true);
      const response = await faturamentoService.pdfNotaFiscal(idNotaFiscal);
      iniciarDownload(new Blob([response]), `${idNotaFiscal}.pdf`);
    } catch (error) {
      toastContext.notification("Não foi possível gerar a nota fiscal. Tente novamente. Se o erro persistir entre em contato com a Alumisoft.", "error");
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = () => {
    formRef.current?.submitForm();
    setNovo(false);
  };


  const columns = useMemo(
    () => [
      {
        Header: "Fatura",
        accessor: "documento",
        display: "align-middle",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <span>{format(parseISO(original.vencimento.toString()), 'MMMM', { locale: pt }).toUpperCase()} - {format(parseISO(original.vencimento.toString()), 'yyyy', { locale: pt }).toUpperCase()}</span>
        )
      },
      {
        Header: "Valor",
        accessor: "valor",
        display: "align-middle",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <span>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(original.valor)}</span>
        )
      },
      {
        Header: "Vencimento",
        accessor: "vencimento",
        display: "align-middle",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <span>{format(parseISO(original.vencimento.toString()), 'dd/MM/yyyy', { locale: pt })}</span>
        )
      },
      {
        Header: "Status",
        accessor: "status",
        display: "align-middle",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <>
            {original.status === 0 && (<span className="badge bg-red">Aguardando Pagamento</span>)}
            {original.status === 2 && (<span className="badge bg-green">Pago</span>)}
          </>
        )
      },
      {
        Header: "Estabelecimento",
        accessor: "estabelecimento",
        display: "align-middle"
      },
      {
        Header: "2º Via",
        accessor: "boleto",
        disableSortBy: true,
        display: "text-center column-table-2b",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <>
            {original.status === 0 ? (
              <Button
                title="Segunda Via Boleto"
                variant="ghost-dark"
                className="p-2"
                onClick={() => handleObterBoleto(original)}
              >
                <i className="far fa-file-alt icon m-0"></i>
              </Button>
            ) : (<span> - </span>)}

          </>
        ),
      },
      {
        Header: "Nota Fiscal",
        accessor: "notafiscal",
        disableSortBy: true,
        display: "text-center column-table-2b",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
          <>
            {original.statusElaboracao === 1 ? (
            <Button
              title="Download PDF"
              variant="ghost-dark"
              className="p-2"
              onClick={() => handleObterNofaFiscal(original)}
            >
              <i className="fas fa-file-invoice-dollar icon m-0"></i>
            </Button>
            ) : (<span> - </span>)}
            {original.temXMLNFSE === 1 ? (
              <Button
                title="Download XML"
                variant="ghost-dark"
                className="p-2"
                onClick={() => handleObterXMLNofaFiscal(original)}
              >
                <i className="fas fa-file icon m-0"></i>
              </Button>
            ) : (<span> - </span>)}
          </>
        ),
      },
      {
        Header: "",
        accessor: "tipoDocumento",
        display: "align-middle",
        Cell: ({ row: { original } }: { row: { original: IFinanceiroTituloReceber } }) => (
           <>
            {original.tipoDocumento === 'Nota Fiscal' && (<span className="badge bg-gray">N</span>)}
            {original.tipoDocumento === 'Provisão de Adiantamento' && (<span className="badge bg-gray">P</span>)}
          </>
        )
      },
    ],
    []
  );

  return (
    <>
      <div className="content py-3">
        <div className="container-xl mb-3">
          <div className="page-header">
            <div className="row">
            <h1>Faturas</h1>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col">
                  <div className={'dimmer' + (tableLoading ? ' active' : '')}>
                    <div className="loader"></div>
                    <div className={"dimmer-content" + (tableLoading ? ' medium-box-loader' : '')} >
                      <small>*Lista dos últimos dois anos</small>
                      {tableData && (
                        <DataTableTS
                          columns={columns}
                          data={tableData}
                          bordered={false}
                          initialState={{
                            sortBy: [
                              {
                                id: "vencimento",
                                desc: true,
                              },
                            ],
                          }}
                        />
                      )}
                      <DataNotFound
                        visible={
                          tableLoading == false ? tableData?.length === 0 : false
                        }
                      ></DataNotFound>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Cor */}
      <Modal size="lg" show={show} onHide={handleClose} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{novo ? "Adicionar Cor" : "Editar Cor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Fatura;
